import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { IdentityErrorService } from 'helpers/IdentityErrorService';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { generateGUID } from 'utils';
import { findDepartments } from 'utils/employeeUtils';

import { ScheduleService } from 'components/services/ScheduleService';
import { EmployeeAttainmentTemplate } from 'constants/EmployeeAttainmentTemplate';
import { AttainmentViewComponent } from 'ui/AttainmentViewComponent';
import ConfirmationDialog from 'ui/shared/ConfirmationDialog';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';
import { TimeViewComponent } from 'ui/TimeViewComponent';

import {
  AttainmentService,
  DepartmentsService,
  EmployeesService,
  LicenseService
} from '../../components/services';
import AuthService from '../../components/services/AuthService';
import { EmployeeScheduleTemplate } from '../../constants';
import { EmployeeSchema } from '../../constants/schemas';
import { useApiCall } from '../../hooks/useApiCall';
import { normalizeLicense } from '../../state/license/licenseSlice';
import { setLicenseInfo } from '../../state/license/licenseSlice';
import { RootState } from '../../state/store';
import { AppDispatch } from '../../state/store';
import {
  ComplexRoleUI,
  Employee,
  Log,
  PersonSchedules,
  RoleUI,
  TreeNode
} from '../../types/interfaces';
import { ObjectForSelection } from '../../types/interfaces/ObjectForSelection';
import { EmployeeTriggerType } from '../../types/interfaces/triggers';
import { EmployeeProps } from '../../types/props';

import AccessDialogContent from './AccessDialogContent';
import DepartmentTab from './DepartmentTab';
import MainTab from './MainTab';
import StartPointDialog from './StartPointDialog';

interface RefInterface {
  handleCancel: () => void;
}

const EmployeeCard = React.forwardRef<RefInterface | null, EmployeeProps>(
  (
    {
      employeeProps,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog,
      withoutButtons,
      withoutDialog,
      showToastOnSave,
      onDirtyChange
    }: EmployeeProps,
    ref
  ) => {
    const methods = useForm<Employee>({
      resolver: zodResolver(EmployeeSchema),
      defaultValues: employeeProps
    });
    const dispatch = useDispatch<AppDispatch>();
    const {
      register,
      trigger,
      reset,
      unregister,
      handleSubmit,
      watch,
      clearErrors,
      setValue,
      formState: { errors, dirtyFields, isDirty }
    } = methods;

    const userRole = watch('userRole');
    const inheritedRoles = watch('inheritedRoles');
    const isUser = watch('isUser');
    const employeeId = watch('id');
    const isTwoFactorEnabled = watch('isTwoFactorEnabled');
    const employeeFullName = watch('fullName');
    const employeeLogin = watch('login');
    const isCustomProductivity = watch('isCustomProductivity');
    const rolesDepartments = watch('rolesDepartments');
    const isImported = watch('isImported');
    const hasPassword = watch('hasPassword');
    const personSchedules = watch('personSchedules');
    const startLongitude = watch('startLongitude');
    const startLatitude = watch('startLatitude');
    const attainments = watch('attainments');

    const licenseInfo = useSelector((state: RootState) => state.license.info);
    const isUsersLimitReached = useMemo(() => {
      const current = licenseInfo?.currentUsers;
      const max = licenseInfo?.maxUsers;
      return current !== undefined && max !== undefined && current >= max;
    }, [licenseInfo?.currentUsers, licenseInfo?.maxUsers]);

    const isUserToggleDisabled = useMemo(() => {
      return employeeProps?.isUser !== true && isUsersLimitReached;
    }, [employeeProps?.isUser, isUsersLimitReached]);

    const usersLimitMessage = useMemo(() => {
      if (!isUserToggleDisabled) return undefined;
      const current = licenseInfo?.currentUsers;
      const max = licenseInfo?.maxUsers;
      if (current === undefined || max === undefined) {
        return 'Пользователей больше создать нельзя';
      }
      return `Пользователей больше создать нельзя. Достигнут лимит: ${current}/${max}`;
    }, [
      isUserToggleDisabled,
      licenseInfo?.currentUsers,
      licenseInfo?.maxUsers
    ]);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isStartPointDialogOpen, setIsStartPointDialogOpen] = useState(false);
    const [tempLatitude, setTempLatitude] = useState<number | null>(null);
    const [tempLongitude, setTempLongitude] = useState<number | null>(null);

    const handleOpenStartPointDialog = () => {
      setTempLatitude(startLatitude ?? null);
      setTempLongitude(startLongitude ?? null);
      setIsStartPointDialogOpen(true);
    };

    const handleApplyStartPoint = (lat: number | null, lon: number | null) => {
      setValue('startLatitude', lat, {
        shouldDirty: true,
        shouldValidate: true
      });
      setValue('startLongitude', lon, {
        shouldDirty: true,
        shouldValidate: true
      });
      setIsStartPointDialogOpen(false);
    };

    const shouldShowImportWarning =
      isUser &&
      isImported &&
      !hasPassword &&
      !errors?.password &&
      !dirtyFields.isUser;

    const roleTags = useMemo(() => {
      return (
        userRole?.map(
          (role: ComplexRoleUI): TreeNode => ({
            key: role.id.toString(),
            label: role.name
          })
        ) ?? []
      );
    }, [userRole]);

    const inheritedRoleTags = useMemo(() => {
      return (
        inheritedRoles?.map(
          (role: ComplexRoleUI): ObjectForSelection => ({
            id: role.id,
            label: role.name
          })
        ) ?? []
      );
    }, [inheritedRoles]);

    const [departments, setDepartments] = useState<TreeNode[]>([]);
    const [schedulesOptions, setSchedulesOptions] = useState<TreeNode[]>([]);
    const [attainmentOptions, setAttainmentOptions] = useState<TreeNode[]>([]);
    const [userLogs, setUserLogs] = useState<Log[]>([]);
    const [userErrors, setUserErrors] = useState<string>('');

    const [dialogVisible, setDialogVisible] = useState<boolean>(false);
    const [newUser, setNewUser] = useState<boolean>(true);
    const [sendTwoFactorDisabled, setSendTwoFactorDisabled] =
      useState<boolean>(false);
    const [selectedDepartments, setSelectedDepartments] = useState<TreeNode[]>(
      []
    );

    const getEmployeeCallback = useCallback(
      async (departments: TreeNode[] | undefined) => {
        setIsLoading(true);
        try {
          if (employeeId) {
            const result = await EmployeesService.getEmployeeById(
              employeeId as number
            );
            const data = result.data;
            const departmentIds = data?.departmentIds ?? [];
            if (data?.productivity !== undefined) {
              originalProductivity.current = data.productivity;
            }
            originalIsCustomProductivity.current =
              data?.isCustomProductivity ?? true;
            if (departmentIds.length > 0 && departments)
              setSelectedDepartments(
                departmentIds.flatMap(
                  (x) =>
                    departments.flatMap((d) =>
                      findDepartments(d, x.toString())
                    ) ?? []
                )
              );
            if (data?.isUser && employeeId) {
              const logs = await EmployeesService.getEmloyessLogs(
                employeeId as number
              );
              if (logs.data && logs.data.length > 0) {
                const formatDate = (dateString: string): string => {
                  const date = new Date(dateString);

                  const formatted =
                    `${date.getDate().toString().padStart(2, '0')}.` +
                    `${(date.getMonth() + 1).toString().padStart(2, '0')}.` +
                    `${date.getFullYear()} ` +
                    `${date.getHours().toString().padStart(2, '0')}:` +
                    `${date.getMinutes().toString().padStart(2, '0')}:` +
                    `${date.getSeconds().toString().padStart(2, '0')}`;
                  return formatted;
                };

                const formatedPlatform = (platform: string): string => {
                  switch (platform) {
                    case 'Web':
                      return 'через web-версию';
                    case 'Aurora':
                      return 'с Аврора';
                    case 'Android':
                      return 'с Android';
                    default:
                      return 'через мобильное приложение';
                  }
                };

                const formattedLogs = logs.data.map((log) => ({
                  ...log,
                  date: formatDate(log.date),
                  platform: formatedPlatform(log.platform)
                }));

                setUserLogs(formattedLogs);
              } else {
                setUserLogs([]);
              }
            }
            if (!data?.login) {
              setNewUser(true);
            } else {
              setNewUser(false);
            }
            setValue('surname', data?.surname ?? '');
            setValue('name', data?.name ?? '');
            setValue('middleName', data?.middleName ?? '');
            setValue('personnelNumber', data?.personnelNumber ?? '');
            setValue('department', data?.department);
            setValue('phone', data?.phone ?? '');
            setValue('email', data?.email ?? '');
            setValue('productivity', data?.productivity ?? '');
            setValue(
              'isCustomProductivity',
              data?.isCustomProductivity ?? true
            );
            setValue('isUser', data?.isUser ?? false);
            setValue('login', data?.login ?? '');
            setValue('isTwoFactorEnabled', data?.isTwoFactorEnabled ?? false);
            setValue('startLongitude', data?.startLongitude ?? null);
            setValue('startLatitude', data?.startLatitude ?? null);
            setValue('userRole', data?.userRole ?? []);
            setValue('inheritedRoles', data?.inheritedRoles ?? []);
            setValue('rolesDepartments', data?.rolesDepartments ?? []);
            setValue('personSchedules', data?.personSchedules ?? []);
            setValue('attainments', data?.attainments ?? []);
          } else if (
            employeeProps.departmentIds &&
            employeeProps.departmentIds.length > 0 &&
            departments
          ) {
            setSelectedDepartments(
              employeeProps.departmentIds.flatMap(
                (x) =>
                  departments.flatMap((d) =>
                    findDepartments(d, x.toString())
                  ) ?? []
              )
            );
          }
        } catch (error) {
          console.error('Error loading employee:', error);
        } finally {
          setIsLoading(false);
        }
      },
      [employeeId, employeeProps.departmentIds, setValue]
    );

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
      // Require password only when creating a new user
      if (
        dirtyFields.password ||
        (newUser && dirtyFields.login && employeeLogin)
      ) {
        setValue('requirePassword', true);
      } else {
        setValue('requirePassword', false);
      }
    }, [
      dirtyFields.password,
      dirtyFields.login,
      employeeLogin,
      newUser,
      setValue
    ]);

    const getDepartmentsCallback = useCallback(async () => {
      const result = await DepartmentsService.getDepartments({
        withSelectAll: true,
        all: true
      });
      setDepartments(result.data ?? []);
      return result.data;
    }, []);

    const getData = useCallback(async () => {
      const departments = await getDepartmentsCallback();
      await getEmployeeCallback(departments);
    }, [getDepartmentsCallback, getEmployeeCallback]);

    useApiCall(getData);

    const isValueSetRef = useRef<boolean>(false); // Tracks if setValue was already called
    const previousRolesDepartments = useRef<any>(null); // Tracks previous value of rolesDepartments
    const originalProductivity = useRef<string | number>('');
    const originalIsCustomProductivity = useRef(true);

    useEffect(() => {
      // Execute only if the conditions are met and `isUser` is dirty
      if (
        dirtyFields.isUser && // Check if `isUser` is a dirty field
        isUser && // The current value of `isUser` is true
        !isValueSetRef.current &&
        selectedDepartments.length > 0 &&
        departments.length > 0
      ) {
        if (
          JSON.stringify(previousRolesDepartments.current) !==
          JSON.stringify(selectedDepartments)
        ) {
          setValue('rolesDepartments', selectedDepartments, {
            shouldDirty: true,
            shouldValidate: true
          });
          // Update refs to prevent re-updating
          previousRolesDepartments.current = selectedDepartments;
          isValueSetRef.current = true;
        }
      }
      // Reset isValueSetRef after dependencies change if `isUser` becomes false
      if (!isUser) {
        isValueSetRef.current = false;
      }
    }, [
      isUser,
      selectedDepartments,
      departments,
      dirtyFields.isUser,
      setValue
    ]);

    useEffect(() => {
      const fetchSchedules = async () => {
        const resultScheduleS =
          await ScheduleService.getSchedulesForSelection();
        const resultAttainment =
          await AttainmentService.getAttainmentsForSelection();

        setSchedulesOptions(resultScheduleS.data.value ?? []);

        setAttainmentOptions(resultAttainment.data.value?.itemsList ?? []);
      };
      fetchSchedules();
      if (employeeId) {
      }
    }, [employeeId]);

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [showCodeDialog, setShowCodeDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showProductivityDialog, setShowProductivityDialog] = useState(false);
    const [productivityCalculationLoading, setProductivityCalculationLoading] =
      useState(false);
    const toast = useRef<Toast>(null);
    const isFirstRender = useRef(true);

    const credentialsErrors = useMemo(() => {
      if (errors.password || errors.login) {
        return true;
      } else {
        return false;
      }
    }, [errors.password, errors.login, isUser]);

    const attainmentsErrors = useMemo(() => {
      if (errors.attainments) {
        return true;
      } else {
        return false;
      }
    }, [errors.attainments]);

    const districtErrors = useMemo(() => {
      if (errors.department) {
        return true;
      } else {
        return false;
      }
    }, [errors.department]);

    const mainErrors = useMemo(() => {
      if (
        errors.personnelNumber ||
        errors.surname ||
        errors.name ||
        errors.middleName ||
        errors.phone ||
        errors.email ||
        errors.productivity ||
        userErrors
      ) {
        return true;
      } else {
        return false;
      }
    }, [
      errors.personnelNumber,
      errors.surname,
      errors.name,
      errors.middleName,
      errors.phone,
      errors.email,
      errors.productivity,
      userErrors
    ]);

    const schedulesErrors = useMemo(() => {
      if (errors.personSchedules) {
        return true;
      } else {
        return false;
      }
    }, [errors.personSchedules]);

    const onSubmit = async (data: Employee) => {
      const isValid = await trigger();
      if (!isValid) {
        return; // Stop execution if the form is not valid
      }
      setUserErrors('');

      const getBackendErrorText = (resp: any): string => {
        const data = resp?.data;
        const candidate =
          data?.error ?? data?.message ?? data?.detail ?? data?.title ?? data;
        if (typeof candidate === 'string' && candidate.trim() !== '') {
          return IdentityErrorService.getErrorMessage(candidate);
        }
        return IdentityErrorService.getErrorMessage('Unknown');
      };
      if (data.password && data.password.length > 0) {
        data.isPasswordHashed = true;
      }
      let hasErrors = false;
      const newFullName = `${data.surname} ${data.name} ${data.middleName}`;
      const newData = {
        ...data,
        departments: selectedDepartments,
        productivity: data.productivity === '' ? 0 : data.productivity,
        startLongitude: data.startLongitude ?? null,
        startLatitude: data.startLatitude ?? null
      };
      newData.fullName = newFullName;
      newData.departmentIds = selectedDepartments.map((x) => parseInt(x.key));
      newData.rolesDepartments = rolesDepartments;

      const initialIsUser = employeeProps?.isUser === true;
      const nextIsUser = Boolean(newData.isUser);
      const isUserChanged = initialIsUser !== nextIsUser;
      const isProductivityChanged = dirtyFields.productivity === true;
      const needsNewUser = nextIsUser && !initialIsUser;
      if (
        isProductivityChanged &&
        !showProductivityDialog &&
        selectedDepartments.length > 0
      ) {
        setShowProductivityDialog(true);
        return;
      }

      if (needsNewUser) {
        try {
          const licenseResp = await LicenseService.getLicense();
          dispatch(setLicenseInfo(licenseResp.data));
          const licenseInfo = normalizeLicense(licenseResp.data);
          if (
            licenseInfo.maxUsers !== undefined &&
            licenseInfo.currentUsers !== undefined &&
            licenseInfo.currentUsers >= licenseInfo.maxUsers
          ) {
            setUserErrors('Пользователей больше создать нельзя');
            return;
          }
        } catch (e) {}
      }
      if (onUpdate && data.id) {
        if (dirtyFields.login) newData.newUser = true;

        const result = await EmployeesService.updateEmployee(
          Number(employeeId),
          newData
        );
        if (
          !result ||
          result.status >= 400 ||
          (result.data && (result.data as any).isSuccess === false) ||
          (result.data as any)?.error
        ) {
          hasErrors = true;
          setUserErrors(getBackendErrorText(result));
        } else if (result.data && result.data.value) {
          const updatedEmployee = result.data.value;
          const fullName =
            `${updatedEmployee.surname} ${updatedEmployee.name} ${updatedEmployee.middleName}`.trim();
          originalProductivity.current = updatedEmployee.productivity ?? '';
          originalIsCustomProductivity.current =
            updatedEmployee.isCustomProductivity ?? true;
          reset({
            ...data,
            ...updatedEmployee,
            id: updatedEmployee.id?.toString(),
            fullName: fullName,
            attainments: updatedEmployee.attainments ?? []
          });
          const twoFactorEnabled = updatedEmployee.isTwoFactorEnabled === true;
          onUpdate(
            updatedEmployee.id,
            {
              ...updatedEmployee,
              fullName: fullName,
              surname: updatedEmployee.surname,
              name: updatedEmployee.name,
              middleName: updatedEmployee.middleName
            },
            twoFactorEnabled,
            newUser
          );
          if (showToastOnSave && toast.current) {
            toast.current.show({
              severity: 'success',
              summary: 'Успешно сохранено',
              detail: 'Сотрудник обновлен',
              life: 3000
            });
          }
          if (isUserChanged) {
            try {
              const licenseResp = await LicenseService.getLicense();
              dispatch(setLicenseInfo(licenseResp.data));
            } catch (e) {}
          }
        }
      }

      if (onAdd && !data.id) {
        newData.id = `${Date.now()}`;
        const result = await EmployeesService.createEmployee(newData);
        if (
          !result ||
          result.status >= 400 ||
          (result.data && (result.data as any).isSuccess === false) ||
          (result.data as any)?.error
        ) {
          hasErrors = true;
          setUserErrors(getBackendErrorText(result));
        } else if (result.data && result.data.value) {
          const createdEmployee = result.data.value;
          originalProductivity.current = createdEmployee.productivity ?? '';
          originalIsCustomProductivity.current =
            createdEmployee.isCustomProductivity ?? true;
          reset({
            ...data,
            ...createdEmployee,
            id: createdEmployee.id?.toString(), // Ensure type match
            fullName: `${createdEmployee.surname} ${createdEmployee.name} ${createdEmployee.middleName}`
          });
          const twoFactorEnabled = createdEmployee.isTwoFactorEnabled === true;
          if (showToastOnSave && toast.current) {
            toast.current.show({
              severity: 'success',
              summary: 'Успешно сохранено',
              detail:
                newData.isUser && twoFactorEnabled
                  ? 'Сотрудник добавлен. Код для завершения регистрации пользователя отправлен на указанную в карточке сотрудника почту'
                  : 'Сотрудник добавлен',
              life: 3000
            });
          }
          onAdd(createdEmployee, twoFactorEnabled);

          if (isUserChanged) {
            try {
              const licenseResp = await LicenseService.getLicense();
              dispatch(setLicenseInfo(licenseResp.data));
            } catch (e) {}
          }
        }
      }

      if (!hasErrors && closeDialog) {
        closeDialog();
      }
    };

    const handleDistrictSelectionChange = (e: TreeNode | TreeNode[]) => {
      if (Array.isArray(e)) {
        setValue('rolesDepartments', e, { shouldDirty: true });
      } else {
        setValue('rolesDepartments', [e], { shouldDirty: true });
      }
    };

    const handleSelectionChange = (
      selection: TreeNode[] | Employee[] | RoleUI[] | null | ComplexRoleUI[],
      selectionType: string
    ) => {
      if (
        selectionType === 'department' &&
        Array.isArray(selection) &&
        selection.every((item) => typeof item === 'object')
      ) {
        setValue('department', selection as TreeNode[], { shouldDirty: true });
        setSelectedDepartments(selection as TreeNode[]);
        if (selection.length == 0 && personSchedules?.length! > 0)
          setValue('personSchedules', [], { shouldDirty: true });
      }
      if (
        selectionType === 'userRole' &&
        Array.isArray(selection) &&
        selection.every((item) => typeof item === 'object')
      ) {
        setValue('userRole', selection as ComplexRoleUI[]);
      }
    };

    const onSelectionChange = (selection: ComplexRoleUI[] | Employee[]) => {
      setValue('userRole', selection as ComplexRoleUI[]);
    };

    const handleInputChange = (currentTrigger: EmployeeTriggerType) => {
      if (currentTrigger === 'login' || currentTrigger === 'email')
        setSendTwoFactorDisabled(true);
      trigger(currentTrigger);
    };

    const handleProductivityChange = () => {
      setValue('isCustomProductivity', true, {
        shouldDirty: true,
        shouldValidate: true
      });
      handleInputChange('productivity');
    };

    const handleProductivityCalculatorClick = useCallback(async () => {
      const id = Number(employeeId);
      if (!Number.isFinite(id) || id <= 0) return;

      setProductivityCalculationLoading(true);
      try {
        const result = await EmployeesService.getEmployeeProductivity(id);
        if (result.data === undefined) {
          throw new Error('Employee productivity response has no value');
        }

        setValue('productivity', result.data.toString(), {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue('isCustomProductivity', false, {
          shouldDirty: true,
          shouldValidate: true
        });
        await trigger('productivity');
      } catch {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось рассчитать производительность сотрудника',
          life: 3000
        });
      } finally {
        setProductivityCalculationLoading(false);
      }
    }, [employeeId, setValue, trigger]);

    const handleCancel = async () => {
      if (Object.keys(dirtyFields).length > 0) {
        setShowDialog(true);
      } else {
        // Reset selectedDepartments if there are no unsaved changes
        setSelectedDepartments([]);
        if (closeDialog) {
          closeDialog();
        }
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleDeleteConfirmation = async () => {
      if (employeeId !== undefined)
        await EmployeesService.deleteEmployee([employeeId as number]);
      if (onDelete) {
        onDelete(employeeId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleProductivityDialogCancel = () => {
      setShowProductivityDialog(false);
    };

    const handleProductivityDialogContinue = () => {
      setShowProductivityDialog(false);
      handleSubmit(onSubmit)();
    };

    const handleProductivityConfirmationCancel = () => {
      setShowProductivityDialog(false);
      setValue('productivity', originalProductivity.current);
      setValue('isCustomProductivity', originalIsCustomProductivity.current);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
    };

    const handleCodeConfirmationCancel = () => {
      setShowCodeDialog(false);
    };

    const handleDeleteSelectedDepartments = (taskId: string | number) => {
      const updated = selectedDepartments.filter(
        (d) => d.key !== taskId.toString()
      );
      handleSelectionChange(updated, 'department');
    };

    const sendNewTwoFactorCodeCallback = useCallback(async () => {
      if (!sendTwoFactorDisabled) {
        const error = await AuthService.sendNewTwoFactorCode(employeeLogin!);
        if (!error.data && toast.current)
          toast.current.show({
            severity: 'success',
            summary: 'Пользователю отправлен код',
            detail:
              'Код пользователя отправлен на указанную в карточке сотрудника почту',
            life: 3000
          });
        setSendTwoFactorDisabled(true);
      }
    }, [employeeLogin, sendTwoFactorDisabled]);

    const handleCodeDialogContinue = useCallback(async () => {
      setShowCodeDialog(false);
      await sendNewTwoFactorCodeCallback();
    }, [sendNewTwoFactorCodeCallback]);

    const handleCodeDialogCancel = useCallback(() => {
      setShowCodeDialog(false);
    }, []);

    const toggleDialogVisibility = () => {
      setDialogVisible(true);
    };

    const closeDialogAccess = () => {
      setDialogVisible(false);
    };

    const handleDeleteCalendar = (calendar: PersonSchedules) => {
      setValue(
        'personSchedules',
        personSchedules?.filter((a) =>
          // For existing records (ID > 0), match by ID
          calendar.id && calendar.id !== 0
            ? a.id !== calendar.id
            : // For new unsaved records (ID === 0), match by GUID
              a.guid !== calendar.guid
        ),
        { shouldValidate: true, shouldDirty: true }
      );
    };

    const handleCalendarUpdate = (
      index: number,
      updated: any,
      validate?: boolean
    ) => {
      const updatedCalendars = [...(personSchedules || [])];
      updatedCalendars[index] = updated;
      setValue('personSchedules', updatedCalendars);
      trigger();
    };

    const handleAddDepartmentCalendar = async () => {
      if (selectedDepartments.length === 0) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Не выбран участок',
          detail: 'Сначала добавьте сотруднику участок во вкладке "Участки"',
          life: 3000
        });
        return;
      }

      const currentSchedules = personSchedules || [];
      const newSchedule = {
        ...EmployeeScheduleTemplate,
        guid: generateGUID()
      };

      setValue('personSchedules', [...currentSchedules, newSchedule], {
        shouldDirty: true
      });
    };

    const handleAddAttainmentCalendar = async () => {
      const currentAttainments = attainments || [];
      const newAttainment = {
        ...EmployeeAttainmentTemplate,
        guid: generateGUID()
      };

      setValue('attainments', [...currentAttainments, newAttainment], {
        shouldDirty: true
      });
    };

    return (
      <FormProvider {...methods}>
        <div className="employee-card employee-card__roles">
          <Toast ref={toast} />
          <TabView>
            <TabPanel
              header="Основная"
              className={mainErrors ? 'error-tab' : ''}
            >
              <MainTab
                register={register}
                errors={errors}
                withoutDialog={withoutDialog}
                handleInputChange={handleInputChange}
                isUser={isUser}
                isUserToggleDisabled={isUserToggleDisabled}
                usersLimitMessage={usersLimitMessage}
                dirtyFields={dirtyFields}
                setValue={setValue}
                clearErrors={clearErrors}
                unregister={unregister}
                userErrors={userErrors}
                isCustomProductivity={isCustomProductivity}
                onProductivityChange={handleProductivityChange}
                onProductivityCalculatorClick={
                  handleProductivityCalculatorClick
                }
                productivityCalculatorDisabled={
                  !employeeId || productivityCalculationLoading
                }
                productivityCalculationLoading={productivityCalculationLoading}
                onOpenStartPointDialog={handleOpenStartPointDialog}
                isLoading={isLoading}
              />
            </TabPanel>
            <TabPanel
              className={districtErrors ? 'error-tab' : ''}
              header="Участки"
            >
              <DepartmentTab
                errors={errors}
                departments={departments}
                selectedDepartments={selectedDepartments}
                handleSelectionChange={handleSelectionChange}
                handleDeleteSelectedDepartments={
                  handleDeleteSelectedDepartments
                }
              />
            </TabPanel>

            <TabPanel
              header="Графики"
              className={schedulesErrors ? 'error-tab' : ''}
            >
              <div className="p-gap-2 employee-personSchedules--wrapper">
                <div
                  className="scrollable-container"
                  id="tags-scroll"
                  style={{
                    width: '100%',
                    height: 'fit-content',
                    maxHeight: '525px',
                    overflow: 'auto'
                  }}
                >
                  <div
                    className="flex flex-column scrollable-content"
                    style={{ width: '99%' }}
                  >
                    <div className="p-d-flex p-flex-column p-gap-2 w-full">
                      <div className=" employee-personSchedules">
                        {personSchedules?.map((schedule, index) => (
                          <TimeViewComponent
                            fieldPrefix="personSchedules"
                            index={index}
                            key={JSON.stringify(schedule)}
                            departmentsOptions={selectedDepartments}
                            showDepartmentField={true}
                            departmentIdField="departmentId"
                            item={schedule}
                            idField="scheduleId"
                            options={schedulesOptions}
                            onDelete={() => handleDeleteCalendar(schedule)}
                            onUpdate={(updated) =>
                              handleCalendarUpdate(index, updated)
                            }
                            errors={errors.personSchedules?.[index]}
                            passedClassName={''}
                            placeholder="Выберите расписание"
                            fieldTranslate={{
                              directTranslate: 'расписание',
                              tooltip: 'расписания'
                            }}
                            useDateOnlyFormat
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="employee-personSchedules__add flex flex-row justify-content-between align-items-center mb-3">
                  <label style={{ height: 'fit-content' }}>
                    {(!personSchedules || personSchedules?.length === 0) &&
                      'График не назначен'}
                  </label>
                  <Button
                    icon="pi pi-plus"
                    onClick={handleAddDepartmentCalendar}
                    tooltip="Назначить график"
                  />
                </div>
              </div>
            </TabPanel>

            <TabPanel
              className={attainmentsErrors ? 'error-tab' : ''}
              header="Навыки"
            >
              <div className="p-gap-2 employee-personAttainment--wrapper">
                <div
                  className="scrollable-container"
                  id="attainments-scroll"
                  style={{
                    width: '100%',
                    maxHeight: '525px',
                    overflow: 'auto'
                  }}
                >
                  <div
                    className="flex flex-column scrollable-content"
                    style={{ width: '99%' }}
                  >
                    <div className="p-d-flex p-flex-column p-gap-2 w-full">
                      <div className="employee-personAttainment">
                        {attainments?.map((attainment, index) => (
                          <AttainmentViewComponent
                            key={attainment.guid || attainment.id}
                            index={index}
                            item={{
                              ...attainment,
                              startDate: attainment.startDate ?? '',
                              endDate: attainment.endDate ?? ''
                            }}
                            options={attainmentOptions}
                            onDelete={() => {
                              setValue(
                                'attainments',
                                attainments.filter((a) =>
                                  attainment.id && attainment.id !== 0
                                    ? a.id !== attainment.id
                                    : a.guid !== attainment.guid
                                ),
                                { shouldValidate: true, shouldDirty: true }
                              );
                            }}
                            onUpdate={(updated) => {
                              const updatedList = [...(attainments || [])];
                              updatedList[index] = updated;
                              setValue('attainments', updatedList, {
                                shouldValidate: true,
                                shouldDirty: true
                              });
                            }}
                            errors={errors.attainments?.[index]}
                            placeholder="Выберите навык"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="employee-personAttainments__add flex flex-row justify-content-between align-items-center mb-3">
                  <label style={{ height: 'fit-content' }}>
                    {(!attainments || attainments.length === 0) &&
                      'Навыки не назначены'}
                  </label>
                  <Button
                    icon="pi pi-plus"
                    onClick={handleAddAttainmentCalendar}
                    tooltip="Добавить навык"
                  />
                </div>
              </div>
            </TabPanel>

            <TabPanel
              className={credentialsErrors ? 'error-tab' : ''}
              header="Доступ в систему"
              disabled={!isUser}
            >
              {errors?.password && (
                <p
                  style={{ marginBottom: '1rem', maxWidth: 'none' }}
                  className={`employee-card-errors card-errors ${
                    errors?.password ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors?.password?.message ?? ' '}
                </p>
              )}
              <div className="flex flex-column md:flex-row gap-3">
                <div className="flex flex-column">
                  <div className="flex flex-column">
                    {userErrors && (
                      <p
                        style={{ marginBottom: '1rem' }}
                        className={`employee-card-errors card-errors ${
                          userErrors ? 'text-red-500' : 'text-transparent'
                        } `}
                      >
                        {userErrors ?? ' '}
                      </p>
                    )}
                    <div className="flex justify-content-between align-items-center gap-3 ">
                      <label htmlFor="login">Логин:</label>
                      <div className="flex align-items-center">
                        <InputText
                          className="user-card__input user-card__input-employee "
                          {...register('login', {
                            onChange: () => handleInputChange('login')
                          })}
                        />
                      </div>
                    </div>
                    <p
                      className={`employee-card-errors card-errors ${
                        errors.login ? 'text-red-500' : 'text-transparent'
                      } `}
                    >
                      {errors.login ? errors.login.message : ' '}
                    </p>
                  </div>

                  <div className="flex flex-column password">
                    <div className="flex flex-column">
                      <div className="flex justify-content-between align-items-center gap-3 ">
                        <label htmlFor="password">Пароль:</label>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <InputText
                            autoComplete="new-password"
                            className="user-card__input user-card__input-employee "
                            placeholder={employeeLogin ? '•••••' : ''}
                            type={isPasswordVisible ? 'text' : 'password'}
                            {...register('password', {
                              onChange: () => handleInputChange('password')
                            })}
                            style={{ paddingRight: 37 }}
                          />
                        </div>
                      </div>
                      <Button
                        className="p-button-text show-password-button"
                        icon={
                          isPasswordVisible ? 'pi pi-eye-slash' : 'pi pi-eye'
                        }
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      />
                    </div>
                  </div>
                  <p
                    className={`employee-card-errors employee-card-errors-password card-errors ${
                      errors.password ? 'text-red-500' : 'text-transparent'
                    } `}
                  >
                    {/* {errors.password ? errors.password.message : ' '} */}
                  </p>
                </div>
              </div>

              <Dialog
                header="Доступ в систему"
                visible={dialogVisible}
                style={{
                  width: '1160px',
                  minWidth: '960px',
                  maxHeight: '900px',
                  height: '797.5px'
                }}
                maximizable
                modal
                onHide={() => setDialogVisible(false)}
              >
                <AccessDialogContent
                  userRole={userRole!}
                  inheritedRoles={inheritedRoles!}
                  inheritedRoleTags={inheritedRoleTags}
                  roleTags={roleTags}
                  rolesDepartments={rolesDepartments!}
                  departments={departments}
                  handleDistrictSelectionChange={handleDistrictSelectionChange}
                  onSelectionChange={onSelectionChange}
                  errors={errors}
                  closeDialog={closeDialogAccess}
                />
              </Dialog>

              <div className="flex flex-column">
                <div className="flex justify-content-between align-items-center gap-3">
                  <div style={{ width: '218px' }}>
                    <Button
                      className="mr-2"
                      rounded
                      severity="secondary"
                      icon="pi pi-cog"
                      label="Доступ в систему"
                      onClick={toggleDialogVisibility}
                      tooltip="Настройка доступа в систему"
                    />
                  </div>
                </div>
                <p
                  className={`employee-card-errors password card-errors ${
                    errors.userRole ? 'text-red-500' : 'text-transparent'
                  } `}
                ></p>
              </div>

              <div className="flex flex-column md:flex-row gap-3">
                <div className="flex flex-column">
                  {!newUser &&
                    employeeId !== undefined &&
                    employeeId !== '' &&
                    isTwoFactorEnabled && (
                      <div className="flex flex-column">
                        <div className="flex justify-content-between align-items-center">
                          <label></label>
                          <Button
                            style={{ width: '220px' }}
                            disabled={sendTwoFactorDisabled}
                            label="Сгенерировать код"
                            onClick={() => {
                              if (!sendTwoFactorDisabled)
                                setShowCodeDialog(true);
                            }}
                            className={
                              sendTwoFactorDisabled
                                ? 'p-button-secondary'
                                : 'p-button-primary'
                            }
                          />
                        </div>
                      </div>
                    )}
                </div>
                <div className="flex flex-column justify-content-end">
                  <p style={{ height: '42px', justifyContent: 'center' }}>
                    {!newUser &&
                    employeeId !== undefined &&
                    employeeId !== '' &&
                    isTwoFactorEnabled &&
                    sendTwoFactorDisabled
                      ? 'Сохраните изменения, тогда сможете отправить код'
                      : ''}
                  </p>
                </div>
              </div>
            </TabPanel>

            <TabPanel
              className={attainmentsErrors ? 'error-tab' : ''}
              header="История входов"
              disabled={!isUser}
            >
              <div className="p-gap-2 employee-personAttainment--wrapper">
                <div
                  className="scrollable-container"
                  id="attainments-scroll"
                  style={{
                    width: '100%',
                    maxHeight: '297px',
                    overflow: 'auto'
                  }}
                >
                  <div
                    className="flex flex-column scrollable-content"
                    style={{ width: '99%' }}
                  >
                    <div className="p-d-flex p-flex-column p-gap-2 w-full">
                      {userLogs && userLogs.length > 0 ? (
                        <>
                          <p
                            style={{
                              color: '#afafaf',
                              fontSize: 12,
                              marginBottom: 10
                            }}
                          >
                            Отображается первый вход и 50 последних
                          </p>
                          {userLogs.map((log: Log) => (
                            <div key={log.id} style={{ marginBottom: 5 }}>
                              Совершен вход: <strong>{log.date}</strong>{' '}
                              {log.platform}
                            </div>
                          ))}
                        </>
                      ) : (
                        'Нет данных'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>
          </TabView>
          <div
            className={
              withoutDialog
                ? 'custom-employee-card-buttons'
                : 'default-card__buttons'
            }
          >
            <div className="flex gap-3">
              <Button
                className="wholeui__roles employee-table-edit-btn__roles"
                label="Сохранить"
                icon="pi pi-check"
                onClick={handleSubmit(onSubmit, (e) => console.log(e))}
              />
              {!withoutButtons && (
                <>
                  <Button
                    label="Удалить"
                    icon="pi pi-trash"
                    onClick={handleDelete}
                    className="p-button-danger"
                  />
                  <Button
                    label="Отмена"
                    icon="pi pi-times"
                    onClick={handleCancel}
                    className="p-button-secondary"
                  />
                </>
              )}
            </div>
          </div>

          <ConfirmationDialog
            visible={showDialog}
            onCancel={() => setShowDialog(false)}
            onContinue={handleConfirmationContinue}
          />

          <Dialog
            visible={showCodeDialog}
            onHide={handleCodeDialogCancel}
            header="Подтверждение"
            pt={withDialogFooterSpacing()}
            footer={
              <div>
                <Button
                  label="Да, сгенерировать код"
                  onClick={handleCodeDialogContinue}
                />
                <Button
                  label="Отмена"
                  onClick={handleCodeConfirmationCancel}
                  className="p-button-secondary"
                />
              </div>
            }
          >
            <div>
              Для пользователя сгенерируется и отправится новый код для
              авторизации. Вы хотите продолжить?
            </div>
          </Dialog>

          <Dialog
            visible={showProductivityDialog}
            onHide={handleProductivityDialogCancel}
            header="Подтверждение"
            pt={withDialogFooterSpacing()}
            footer={
              <div>
                <Button label="Да" onClick={handleProductivityDialogContinue} />
                <Button
                  label="Отмена"
                  onClick={handleProductivityConfirmationCancel}
                  className="p-button-secondary"
                />
              </div>
            }
          >
            <div>
              Производительность участков, в которых данный сотрудник работает
              может быть изменена. Вы хотите продолжить?
            </div>
          </Dialog>

          <DeleteConfirmationDialog
            visible={showDeleteDialog}
            onHide={handleDeleteCancellation}
            onConfirm={handleDeleteConfirmation}
            entityType="сотрудника"
            entityName={employeeFullName}
            warnings={[
              <div key="w1">Сотрудник может быть назначен на наряд.</div>,
              ...(watch('productivity')
                ? [
                    <div key="w2">
                      Производительность участков, в которых работал данный
                      сотрудник, может быть изменена.
                    </div>
                  ]
                : [])
            ]}
          />

          <StartPointDialog
            visible={isStartPointDialogOpen}
            onHide={() => setIsStartPointDialogOpen(false)}
            onApply={handleApplyStartPoint}
            initialLatitude={tempLatitude}
            initialLongitude={tempLongitude}
          />
        </div>
      </FormProvider>
    );
  }
);

EmployeeCard.displayName = 'EmployeeCard';

export default EmployeeCard;
